import { Service, Container } from "typedi";
import { TronManagerService } from "./TronManagerService";
import { MINTSAVER_ADDRESS } from "../constants/config";
import { CONTRACT_ADDRESS } from "../constants/tokens";

interface ITokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  address: string;
}

interface ITransaction {
  from: string;
  to: string;
  block_timestamp: number;
  value: number;
  type: string;
  transaction_id: string;
  token_info: ITokenInfo;
}

export interface ICentricTransaction {
  date: number;
  status: string;
  from: string;
  to: string;
  token: string;
  amount: number;
  action: string;
  direction: string;
  txid: string;
}

class CentricTransaction implements ICentricTransaction {
  date: number;
  status: string;
  from: string;
  to: string;
  token: string;
  amount: number;
  action: string;
  direction: string;
  txid: string;

  constructor(transaction: ITransaction, action: string, address: string) {
    this.date = Number(transaction.block_timestamp);
    this.status = "SUCCESS";
    this.from = transaction.from;
    this.to = transaction.to;
    this.amount = transaction.value;
    this.token = transaction.token_info.symbol;
    this.action = action;
    this.direction = address === transaction.from ? "OUT" : "IN";
    this.txid = transaction.transaction_id;
  }
}

export interface ICentricAccount {
  rise: number;
  cash: number;
  trx: number;
  created: number;
}

class CentricAccount implements ICentricAccount {
  rise: number;
  cash: number;
  trx: number;
  created: number;
  constructor(
    riseBalance: any,
    cashBalance: any,
    trxBalance: number,
    created: number
  ) {
    this.rise = Number(riseBalance);
    this.cash = Number(cashBalance);
    this.trx = Number(trxBalance);
    this.created = created || 0;
  }
}

function transactionToCentricTransactionFactory(
  transaction: ITransaction,
  address: string
) {
  if (!transaction.to || !transaction.from || transaction.type === "Approval") {
    return undefined;
  }
  if (transaction.to === CONTRACT_ADDRESS.rise) {
    if (transaction.from === MINTSAVER_ADDRESS) {
      return new CentricTransaction(transaction, "Burn", address);
    } else {
      switch (transaction.token_info.symbol) {
        case "CNR":
          return new CentricTransaction(transaction, "ConvertToCash", address);
        case "CNS":
          return new CentricTransaction(transaction, "ConvertToRise", address);
        default:
          return undefined;
      }
    }
  } else {
    return new CentricTransaction(transaction, "Transfer", address);
  }
}

function createAccountService() {
  return new AccountService(Container.get(TronManagerService));
}

@Service({ factory: createAccountService })
export class AccountService {
  constructor(private tronManagerService: TronManagerService) {}
  async getTransactions(
    address: string,
    contractAddress: string,
    minTimestamp: number,
    maxTimestamp?: number,
    limit: number = 200,
    maxResults: number = 1000,
    orderBy?: string
  ): Promise<[ICentricTransaction[], number]> {
    const options = {
      contract_address: contractAddress,
      min_timestamp: minTimestamp,
      max_timestamp: maxTimestamp,
      limit: limit,
      order_by: orderBy,
    };
    const max = maxResults;

    const tronService = this.tronManagerService.getTronService();
    const transactions = await tronService.getAccountTrc20Transactions(
      address,
      options,
      max
    );
    transactions.sort(sortDesc);

    const centricTransactions = transactions
      .map((t) => transactionToCentricTransactionFactory(t, address))
      .filter((t): t is CentricTransaction => Boolean(t));
    const count = centricTransactions.length;

    return [centricTransactions, count];
  }

  async getAccount(address: string): Promise<ICentricAccount> {
    const options = {};
    const tronService = this.tronManagerService.getTronService();
    const [accountInfo] = await tronService.getAccountInfo(address, options);
    let trc20s: any[] = [];
    trc20s = accountInfo.trc20;
    let riseBalance: number = 0;
    let cashBalance: number = 0;
    let trxBalance: number = 0;
    [riseBalance] = Object.values(
      trc20s.find((item) => {
        return item[CONTRACT_ADDRESS.rise];
      }) || { balance: 0 }
    );
    [cashBalance] = Object.values(
      trc20s.find((item) => {
        return item[CONTRACT_ADDRESS.cash];
      }) || { balance: 0 }
    );

    trxBalance = accountInfo.balance;
    const created = accountInfo.create_time;

    const centricAccount = new CentricAccount(
      riseBalance,
      cashBalance,
      trxBalance,
      created
    );
    return centricAccount;
  }
}

function sortDesc(a: any, b: any) {
  return b.block_timestamp - a.block_timestamp;
}
